import React, { memo, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import type { ServiceOffering } from '../../../types/backend';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { useServiceOfferingsManagement } from '../../service-offerings/hooks/useServiceOfferingsManagement';
import OfferingDetailPanel from '../../service-offerings/components/OfferingDetailPanel';
import OfferingForm from '../../service-offerings/components/OfferingForm';
import type { ServiceOfferingCreateRequest, ServiceOfferingUpdateRequest } from '../../service-offerings/services/serviceOfferingsService';
import { categoryLabel } from '../../../shared/labels';

interface ServiceAdminTabContentProps {
  isOpen: boolean;
  onCountChange?: (count: number) => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', active: 'Active', deprecated: 'Deprecated',
};

const offeringListConfig: EntityListItemConfig<ServiceOffering> = {
  getId: (o) => o.id,
  getPrimaryText: (o) => o.display_name,
  getSecondaryText: (o) => `${STATUS_LABEL[o.status ?? 'draft'] ?? 'Draft'}${o.owner ? ` · ${o.owner}` : ''}`,
  getSearchText: (o) => [o.name, o.display_name, o.owner ?? ''].join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

const offeringGroupBy = (o: ServiceOffering) => [{
  key: o.category ?? 'other',
  label: categoryLabel(o.category ?? 'other'),
}];

const ServiceAdminTabContent: React.FC<ServiceAdminTabContentProps> = memo(({ isOpen, onCountChange }) => {
  const { state, select, create, update, publish, deprecate } = useServiceOfferingsManagement();
  const { offerings, isLoading, error, selected } = state;

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  React.useEffect(() => {
    onCountChange?.(offerings.length);
  }, [offerings.length, onCountChange]);

  const handleCreate = async (data: ServiceOfferingCreateRequest | ServiceOfferingUpdateRequest) => {
    setCreating(true);
    setCreateError(null);
    try {
      const created = await create(data as ServiceOfferingCreateRequest);
      setShowCreateForm(false);
      select(created);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel<ServiceOffering>
          entities={offerings}
          selectedEntity={selected}
          isNew={showCreateForm}
          saving={creating}
          config={offeringListConfig}
          searchable
          groupBy={offeringGroupBy}
          addButtonText="Add Offering"
          emptyStateText="No offerings yet"
          emptyStateHelperText='Click "Add Offering" to get started'
          onSelect={(o) => { select(o); setShowCreateForm(false); }}
          onAddNew={() => { setShowCreateForm(true); select(null); }}
          isLoading={isLoading}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.paper' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {showCreateForm && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>New Service Offering</Typography>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>{createError}</Alert>
            )}
            <OfferingForm
              isNew
              saving={creating}
              onSave={handleCreate}
              onCancel={() => setShowCreateForm(false)}
            />
          </Box>
        )}

        {!showCreateForm && selected && (
          <OfferingDetailPanel
            offering={selected}
            onUpdate={update}
            onPublish={publish}
            onDeprecate={deprecate}
            onOfferingUpdated={select}
          />
        )}

        {!showCreateForm && !selected && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              Select an offering to view details, or create a new one.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

ServiceAdminTabContent.displayName = 'ServiceAdminTabContent';

export default ServiceAdminTabContent;
